/**
 * ResultsDisplay - Manages results display and area cards
 *
 * Responsibilities:
 * - Display calculation results
 * - Edit specific areas
 * - Manage area cards display
 */
export default class ResultsDisplay {
    constructor(rootElement, templateManager, areaManager = null) {
        this.root = rootElement;
        this.templateManager = templateManager;
        this.areaManager = areaManager;

        this.selectors = {
            area: '[data-substrate-calculator="area"]',
            areaContainer: '[data-substrate-calculator="area-container"]',
            calculateBtn: '[data-substrate-calculator="calculate"]',
            resultsContainer: '[data-substrate-calculator="results-container"]'
        };
    }

    /**
     * Display calculation results
     *
     * @param {Object} data - Calculation data
     * @param {Object} products - Products with packaging
     * @param {Function} formatDimensionsText - Function to format dimensions
     */
    displayResults(data, products, formatDimensionsText) {
        const resultsFragment = this.templateManager.clone('results');

        if (!resultsFragment) {
            console.error('Could not clone results template');
            return;
        }

        const resultsContainer = resultsFragment.querySelector(this.selectors.resultsContainer);

        // Add area cards
        const areaCardsContainer = resultsFragment.querySelector('[data-substrate-calculator="area-cards"]');

        // Safety buffer applies ONLY to substrate, NOT to drainage
        const safetyBufferPercent = products.safetyBuffer || 0;

        // Calculate totals by summing rounded values from each area
        // This ensures header total matches the sum of cards
        let totalSubstrateRounded = 0;
        let totalDrainageRounded = 0;
        let totalVolumeRounded = 0;

        data.areas.forEach((area, index) => {
            // Calculate actual volumes for this area (with safety buffer for substrate)
            const areaSubstrateWithBuffer = (area.substrateVolume || 0) * (1 + (safetyBufferPercent / 100));
            const areaDrainageNoBuffer = area.drainageVolume || 0;  // No safety buffer for drainage
            const areaTotalWithBuffer = areaSubstrateWithBuffer + areaDrainageNoBuffer;

            // Round values for this area (as displayed on card)
            const areaSubstrateRounded = Math.ceil(areaSubstrateWithBuffer);
            const areaDrainageRounded = Math.ceil(areaDrainageNoBuffer);
            const areaTotalRounded = areaSubstrateRounded + areaDrainageRounded;

            // Add to totals
            totalSubstrateRounded += areaSubstrateRounded;
            totalDrainageRounded += areaDrainageRounded;
            totalVolumeRounded += areaTotalRounded;

            const calculatedVolumes = {
                substrate: areaSubstrateWithBuffer,
                drainage: areaDrainageNoBuffer,
                total: areaTotalWithBuffer,
                safetyBuffer: safetyBufferPercent
            };

            const areaCard = this.templateManager.createAreaCard(
                area,
                index,
                formatDimensionsText,
                calculatedVolumes
            );

            if (areaCard) {
                const editBtn = areaCard.querySelector('[data-substrate-calculator="edit-area"]');
                if (editBtn) {
                    editBtn.addEventListener('click', () => this.editArea(index));
                }

                areaCardsContainer.appendChild(areaCard);
            }
        });

        // Now populate header with summed rounded values (matches sum of cards)
        this.templateManager.populate(resultsFragment, {
            'total-liters': `${totalVolumeRounded} litrów`,
            'substrate-display': `${totalSubstrateRounded}l`,
            'drainage-display': `${totalDrainageRounded}l`
        });

        // Hide drainage section if no drainage volume
        if (products.drainage.totalVolume <= 0) {
            // Hide in top summary (substrate-display + drainage-display)
            const substrateDisplay = resultsFragment.querySelector('[data-substrate-calculator="substrate-display"]');
            if (substrateDisplay) {
                // The structure is: span(substrate+label), span(+), span(drainage+label)
                // Hide the "+" span and the drainage span
                const parent = substrateDisplay.closest('p');
                if (parent) {
                    const children = Array.from(parent.children);
                    const substrateWrapperIndex = children.findIndex(el =>
                        el.contains(substrateDisplay)
                    );

                    if (substrateWrapperIndex >= 0) {
                        // Hide everything after substrate wrapper (+ and drainage wrapper)
                        for (let i = substrateWrapperIndex + 1; i < children.length; i++) {
                            children[i].style.display = 'none';
                        }
                    }
                }
            }

            // Hide drainage products checkbox section
            const drainageGroup = resultsFragment.querySelector('[data-substrate-calculator="drainage-checkbox"]')?.closest('.mb-6');
            if (drainageGroup) {
                drainageGroup.style.display = 'none';
            }
        }

        return resultsFragment;
    }

    /**
     * Edit a specific area
     *
     * @param {number} areaIndex - Index of area to edit
     */
    editArea(areaIndex) {
        this.editAreas(areaIndex);
    }

    /**
     * Return to edit mode - show areas and buttons, hide results
     * Optionally scroll to specific area
     *
     * @param {number|null} areaIndex - Optional area index to scroll to
     */
    editAreas(areaIndex = null) {
        const results = this.root.querySelector(this.selectors.resultsContainer);
        const areaContainer = this.root.querySelector(this.selectors.areaContainer);
        const calculateBtn = this.root.querySelector(this.selectors.calculateBtn);

        // Show areas container
        if (areaContainer) {
            areaContainer.style.display = 'block';
        }

        // Show calculate button
        if (calculateBtn) {
            calculateBtn.style.display = 'block';
        }

        // Hide results instead of removing (so they can be updated when drainage changes)
        if (results) {
            results.style.display = 'none';
        }

        // If specific area requested, scroll to it and highlight
        if (areaIndex !== null) {
            // Highlight the area being edited
            if (this.areaManager) {
                this.areaManager.highlightArea(areaIndex);
            }

            const allAreas = this.root.querySelectorAll(this.selectors.area);
            if (allAreas[areaIndex]) {
                allAreas[areaIndex].scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }

    /**
     * Remove results from DOM
     */
    removeResults() {
        const results = this.root.querySelector(this.selectors.resultsContainer);
        if (results) {
            results.remove();
        }
    }
}
